import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { AnimatedNumber } from '@sorare/core/src/atoms/animations/AnimatedNumber';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { RivalsLightning } from '@sorare/core/src/components/rivals/RepScore/RivalsLightning';

import { Layout } from 'components/stories/rivals/leaderboards/Layout';
import {
  FocusedStatType,
  ProfileOverview,
} from 'components/stories/rivals/leaderboards/ProfileOverview';

import {
  RepChange_FootballRivalsAggregatedArenaStory,
  RepChange_footballRivalsCurrentManager,
} from './__generated__/index.graphql';

const Headline48 = styled.span`
  font: var(--t-48);
  line-height: 0.8rem;
`;
const Difference = styled(Horizontal).attrs({ gap: 0 })`
  align-items: baseline;
`;

type Props = {
  story: RepChange_FootballRivalsAggregatedArenaStory;
  manager: RepChange_footballRivalsCurrentManager;
};
export const RepChange = ({ story, manager }: Props) => {
  const { newPoints, previousPoints, previousRanking } = story;

  const isWin = newPoints > previousPoints;
  const patternColor = isWin ? '#f1ff00' : '#ff4e22';
  const diamondColor = isWin
    ? 'var(--c-rgb-rivals-reputation)'
    : 'var(--c-rgb-rivals-loss)';

  return (
    <Layout
      diamondColor={diamondColor}
      patternColor={patternColor}
      manager={manager}
    >
      <Vertical gap={4} center>
        <LabelL color="var(--c-neutral-800)" bold uppercase>
          {isWin ? (
            <FormattedMessage
              id="RepChange.bodyTitle.repEarned"
              defaultMessage="Rep Earned"
            />
          ) : (
            <FormattedMessage
              id="RepChange.bodyTitle.repLost"
              defaultMessage="Rep Lost"
            />
          )}
        </LabelL>
        <ProfileOverview
          manager={manager}
          focus={FocusedStatType.REP}
          shadowColor={
            isWin ? 'var(--c-rivals-reputation)' : 'var(--c-rivals-loss)'
          }
          rankBefore={previousRanking}
          rankAfter={previousRanking}
          repBefore={previousPoints}
          repAfter={newPoints}
        />
        <Difference>
          <Headline48>
            <AnimatedNumber
              value={0}
              animationProps={{
                from: { animatedValue: newPoints - previousPoints },
                config: { duration: 800 },
                delay: 1800,
              }}
            />
          </Headline48>
          <RivalsLightning fill="var(--c-rivals-reputation)" />
        </Difference>
      </Vertical>
    </Layout>
  );
};

RepChange.fragments = {
  footballRivalsAggregatedArenaStory: gql`
    fragment RepChange_FootballRivalsAggregatedArenaStory on FootballRivalsAggregatedArenaStory {
      id
      previousPoints
      newPoints
      previousRanking
    }
  ` as TypedDocumentNode<RepChange_FootballRivalsAggregatedArenaStory>,
  footballRivalsCurrentManager: gql`
    fragment RepChange_footballRivalsCurrentManager on FootballRivalsCurrentManager {
      id
      ...ProfileOverview_footballRivalsCurrentManager
      ...Layout_footballRivalsCurrentManager
    }
    ${Layout.fragments.footballRivalsCurrentManager}
    ${ProfileOverview.fragments.footballRivalsCurrentManager}
  ` as TypedDocumentNode<RepChange_footballRivalsCurrentManager>,
};
